import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import NotFound from "./pages/404";
import { Suspense, useEffect, useCallback, lazy } from "react";
import { Slide, toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useStoreActions } from "easy-peasy";
import { jwtDecode } from "jwt-decode"; // Correct import
import { appRoutes } from "./routes";
import ScrollToTop from "./components/ScrollToTop";
import axios from "axios";
import { useTranslation } from "react-i18next";
import About from "./pages/About";
import { getAuthKey } from "./utils/apiUtils";
import { clearLocalStorage } from "./utils/functions";
import { CircleDashedIcon } from "lucide-react";
import VerifyAccount from "./pages/VerifyAccount";
import CheckoutPage from "./pages/Checkout";
const CreateMaster = lazy(() => import("./pages/users/CreateMaster"));
const ForgotPassword = lazy(() => import("./pages/users/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/users/ResetPassword"));
const OrderEstimate = lazy(() => import("./pages/OrderEstimate"));
const Login = lazy(() => import("./pages/Login"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const Pricing = lazy(() => import("./pages/Pricing"));
const AccountCreate = lazy(() => import("./pages/AccountCreate"));
const TrialActivate = lazy(() => import("./pages/TrialActivate"));
const Feedback = lazy(() => import("./pages/Feedback"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));

function App() {
  const authKey = getAuthKey();
  const setUser = useStoreActions((state) => state.setUser);
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  document.body.dir = i18n.dir();

  // Utility function to check if the token is expired
  const isTokenExpired = (token) => {
    try {
      const decoded = jwtDecode(token);
      return Date.now() >= decoded.exp * 1000;
    } catch (error) {
      console.error("Token parsing error:", error);
      return true;
    }
  };

  // useEffect for token validation and axios interceptor
  useEffect(() => {
    const token = getAuthKey();

    if (token && !isTokenExpired(token)) {
      // Token is valid, set user details
      const decoded = jwtDecode(token);
      setUser({
        id: decoded.id,
        email: decoded.email,
        branch: decoded.branch,
        role: Array.isArray(decoded.role)
          ? decoded.role.map((r) => ({ type: r }))
          : [{ type: decoded.role }],
        language: decoded.language,
      });
    } else if (token && isTokenExpired(token)) {
      clearLocalStorage();
      navigate("/");
      toast.warn("Session expired. Please log in again.");
    }

    // Axios interceptor to handle session expiry or unauthorized requests
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response) {
          if (error.response.status === 401) {
            clearLocalStorage();
            navigate("/home");
            toast.warn("Session expired. Please log in again.");
          } else if (error.response.status === 403) {
            navigate("/home");
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [navigate, setUser]);

  // Utility function to check if user is authenticated
  const isAuthenticated = useCallback(() => {
    return authKey && !isTokenExpired(authKey);
  }, [authKey]);

  return (
    <Suspense
      fallback={
        <div className="w-screen h-screen flex items-center justify-center">
          <CircleDashedIcon
            size={60}
            className="animate-spin text-khamco-ribbon"
          />
        </div>
      }
    >
      <ScrollToTop />
      <Routes>
        {/* Public Routes */}
        <Route exact path="/about" element={<About />} />
        <Route exact path="/feedback" element={<Feedback />} />
        <Route exact path="/terms" element={<Terms />} />
        <Route exact path="/privacy" element={<Privacy />} />
        <Route exact path="/" element={<LandingPage />} />
        <Route exact path="/pricing" element={<Pricing />} />
        <Route exact path="/checkout" element={<CheckoutPage />} />
        <Route exact path="/blog" element={<Blog />} />
        <Route exact path="/blog/:id" element={<BlogArticle />} />
        <Route exact path="/account/create/:token" element={<AccountCreate />} />
        <Route exact path="/trial/activate/:token" element={<TrialActivate />} />
        <Route exact path="/users/login" element={<Login/>} />
        <Route exact path="/users/verify-account/:token" element={<VerifyAccount/>} />
        <Route exact path="/orders/estimate/:id" element={<OrderEstimate />} />
        <Route
          exact
          path="/users/forgot-password"
          element={<ForgotPassword />}
        />
        <Route
          exact
          path="/users/reset-password/:token"
          element={<ResetPassword />}
        />
        <Route exact path="/users/create-master" element={<CreateMaster />} />
        {/* Protected Routes */}
        {appRoutes.map((route) => (
          <Route
            key={route.path}
            exact
            path={route.path}
            element={
              isAuthenticated() ? (
                <route.component />
              ) : (
                <Navigate to="/" />
              )
            }
          />
        ))}

        {/* Misc Routes */}
        <Route exact path="/unauthorized" element={<NotFound />} />
        <Route exact path="*" element={<NotFound />} />
      </Routes>
      <ToastContainer
        position="top-right"
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
        autoClose={5000}
      />
    </Suspense>
  );
}

export default App;
